import React, { forwardRef, useEffect, useMemo, useRef } from "react";

import { Icon, Input, useSummaryContext } from "@braze/beacon-core";

import { updateInputDescription, updateInputParameters } from "@lib/agenticAi/sharedToolContracts";

import useI18nNamespace from "@services/useI18nNamespace";
import { useOperatorEntityId } from "@services/useOperatorEntityId";
import { useRegisterChangeTool } from "@services/useRegisterChangeTool";

import { DiffDisplay } from "@components/AgenticAi/components/DiffDisplay";
import { OperatorDescriptionWrapper } from "@components/OperatorDescriptionWrapper";

import type { BrazeInputProps } from "./types";

export const BrazeInput = forwardRef<HTMLInputElement, BrazeInputProps>(
  (
    {
      onChange,
      onBlur,
      operatorLabel,
      operatorDisabledReason,
      operatorValidate,
      disabled,
      readOnly,
      isSummary,
      placeholder,
      value,
      name,
      sensitive,
      type = "text",
      ...props
    },
    ref
  ) => {
    const { t } = useI18nNamespace("braze-input");
    const registerChangeTool = useRegisterChangeTool({ operatorDisabledReason });
    const id = useOperatorEntityId("braze-input");
    const shouldRedactValue = sensitive || type === "password";
    const summaryContext = useSummaryContext();
    const isInSummaryMode = isSummary || summaryContext.isSummary;

    // Use refs to avoid re-registering the tool when callbacks or values change.
    // This prevents infinite loops when parent components pass inline callbacks.
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const onBlurRef = useRef(onBlur);
    onBlurRef.current = onBlur;
    const valueRef = useRef(value);
    valueRef.current = value;
    const operatorValidateRef = useRef(operatorValidate);
    operatorValidateRef.current = operatorValidate;

    useEffect(() => {
      if (disabled || isInSummaryMode || readOnly) {
        return;
      }

      const cleanup = registerChangeTool({
        toolEntityId: id,
        name: "update_input",
        description: updateInputDescription,
        parameters: updateInputParameters,
        callback: (params: { text: string }) => {
          const rejection = operatorValidateRef.current?.(params.text);
          if (rejection) {
            throw new Error(rejection);
          }
          onChangeRef.current?.({ target: { value: params.text, name } } as React.ChangeEvent<HTMLInputElement>);
          // Trigger blur after change so forms using onBlur validation mode re-validate and clear errors
          onBlurRef.current?.({ target: { value: params.text, name } } as React.FocusEvent<HTMLInputElement>);
          return shouldRedactValue ? "Input was successfully updated." : `Input was set to "${params.text}".`;
        },
        options: {
          toolGroup: "Agentic Creation & Editing",
          labels: {
            ready: t("update-input-ready", "Preparing to update input"),
            running: t("update-input-running", "Updating input"),
            complete: t("update-input-complete", "Updated input"),
            failed: t("update-input-failed", "Failed to update input"),
            stopped: t("update-input-stopped", "Stopped updating input"),
          },
          requiresApproval: "yes",
          argumentsSummaryText: () => operatorLabel,
          renderApproval: ({ args: { text } }) => (
            <DiffDisplay
              title={operatorLabel}
              original={String(valueRef.current || "")}
              modified={text}
              placeholder={t("empty-placeholder", "Empty")}
            />
          ),
          toolIcon: <Icon name="pen-square" />,
        },
      });

      return () => {
        cleanup();
      };
    }, [
      registerChangeTool,
      id,
      operatorLabel,
      placeholder,
      t,
      disabled,
      isInSummaryMode,
      readOnly,
      name,
      shouldRedactValue,
    ]);

    const operatorDescription = useMemo(() => {
      const input = document.createElement("input");
      input.type = type;
      input.id = id;
      input.setAttribute("aria-label", operatorLabel);

      if (placeholder) {
        input.setAttribute("placeholder", placeholder);
      }

      if (disabled) {
        input.disabled = true;
      }

      if (readOnly || isInSummaryMode) {
        input.readOnly = true;
      }

      if (shouldRedactValue) {
        input.setAttribute("value", "[redacted]");
      } else if (value) {
        input.setAttribute("value", String(value));
      }

      return input.outerHTML;
    }, [id, operatorLabel, disabled, readOnly, isInSummaryMode, placeholder, value, shouldRedactValue, type]);

    return (
      <OperatorDescriptionWrapper description={operatorDescription}>
        <Input
          {...props}
          name={name}
          ref={ref}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          readOnly={readOnly}
          isSummary={isInSummaryMode}
          placeholder={placeholder}
          type={type}
          value={value}
        />
      </OperatorDescriptionWrapper>
    );
  }
);
